import * as zod from 'zod';

// eslint-disable-next-line no-restricted-imports
import { CBVoltage } from '@sb/robot';
import { six } from '@sb/utilities';

// A set of arm joint velocities in radians per second, in order from joint 0 to joint 5.
export const ArmJointVelocities = zod.tuple([
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
  zod.number(),
]);

export type ArmJointVelocities = zod.infer<typeof ArmJointVelocities>;

export const NullableArmJointVelocities = zod.tuple(
  six(zod.number().nullable()),
);

export type NullableArmJointVelocities = zod.infer<
  typeof NullableArmJointVelocities
>;

// radians / second
export const ABSOLUTE_MAX_JOINT_SPEEDS: ArmJointVelocities = [
  4.7, 4.7, 5, 6.8, 6.8, 6.8,
];

export const COLLABORATIVE_MAX_JOINT_SPEEDS: ArmJointVelocities = [
  1.8, 1.8, 2.5, 3.4, 3.4, 2.0,
];

export const DEFAULT_MAX_JOINT_SPEEDS = COLLABORATIVE_MAX_JOINT_SPEEDS;

export const THOR_MAX_JOINT_SPEEDS: ArmJointVelocities = [
  2.18, 2.18, 2.69, 4.15, 4.15, 4.15,
];

// Spark firmware maxVelocity (4.85 / 5.75 / 6.9 rad/s) derated by the same
// ~14% headroom Thor's SHG-25 wrist keeps below its firmware limit (4.15/4.85).
export const SPARK_MAX_JOINT_SPEEDS: ArmJointVelocities = [
  4.15, 4.15, 4.92, 5.9, 5.9, 5.9,
];

export const THOR_24V_MAX_JOINT_SPEEDS: ArmJointVelocities = [
  0.9, 0.9, 1.25, 1.55, 1.55, 1.55,
];

export const THOR_42V_MAX_JOINT_SPEEDS: ArmJointVelocities = [
  1.75, 1.75, 2.15, 2.9, 2.9, 2.9,
];

export const ANTIGRAVITY_SPEEDS_DEFAULT: ArmJointVelocities = six(0.3);

// meters / second
export const ABSOLUTE_MAX_TOOLTIP_SPEED = 5;
export const COLLABORATIVE_MAX_TOOLTIP_SPEED = 1.25;
// ISO 10218-1 reduced-speed manual mode: tooltip cap for motion an operator
// drives by hand while holding a 3PE. A regulatory number, independent of the
// ad-hoc/collaborative profiles above.
export const TEACH_MODE_TOOLTIP_VELOCITY_LIMIT = 0.25;
export const DEFAULT_MAX_TOOLTIP_SPEED = COLLABORATIVE_MAX_TOOLTIP_SPEED;
// Max tooltip speed for the Thor Default factory preset. Used as the UI/backend
// ceiling for Thor when the `unleashThor` flag is off.
export const THOR_DEFAULT_PRESET_TOOLTIP_SPEED = 2.5;

// meters / second squared
export const DEFAULT_MAX_TOOLTIP_ACCELERATION = null; // Safety limit
export const DEFAULT_WELD_TOOLTIP_ACCELERATION = 0.05; // 50 mm/s^2
export const MAX_WELD_TOOLTIP_ACCELERATION = 0.4; // 400 mm/s^2
export const DEFAULT_CONSTANT_SPEED_TOOLTIP_ACCELERATION = 0.25; // 250 mm/s^2

/** Minimum tooltip acceleration for constant-speed motions (m/s²). */
export const MIN_CONSTANT_SPEED_TOOLTIP_ACCELERATION = 0.001;

/** Maximum tooltip acceleration for constant-speed motions (m/s²). */
export const MAX_CONSTANT_SPEED_TOOLTIP_ACCELERATION = 2.0;

export const TESTRUN_MAX_JOINT_SPEEDS: ArmJointVelocities = six(0.25);

export function getThorMaxJointSpeedsForCBVoltage(
  voltage: CBVoltage | undefined,
): ArmJointVelocities {
  switch (voltage) {
    case CBVoltage.enum.V42:
      return THOR_42V_MAX_JOINT_SPEEDS;
    case CBVoltage.enum.V48:
      return THOR_MAX_JOINT_SPEEDS;
    case CBVoltage.enum.V24:
    default:
      return THOR_24V_MAX_JOINT_SPEEDS;
  }
}

export function getThorTooltipSpeedCapForCBVoltage(
  voltage: CBVoltage | undefined,
  useUnleashThor: boolean,
): number {
  return voltage === CBVoltage.enum.V48 && useUnleashThor
    ? ABSOLUTE_MAX_TOOLTIP_SPEED
    : THOR_DEFAULT_PRESET_TOOLTIP_SPEED;
}
